import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, InputAdornment, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';

import { FormProvider, RHFTextField } from '@app/components';
import { Strings } from '@app/constants';
import { SignUpSchema } from '../validators';
import { typography } from '@app/theme/typography';
import { SvgColor } from '@app/components/svg-color';
import { selectLoginState } from '@app/modules/auth/selectors';
import { useRequestState } from '@app/hooks';
import { IMyProfileForm } from '../types';
import { updateProfileForm } from '../slice';
import { selectMyProfileState } from '../selectors';

// --------------------------------------------

const MyProfile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data } = useSelector(selectLoginState);
  const { loading } = useRequestState({
    stateSelector: selectMyProfileState,
    errorShown: true,
    successMessageShown: true,
  });

  const methods = useForm<IMyProfileForm>({
    resolver: yupResolver(SignUpSchema),
    defaultValues: {
      name: '',
      email: '',
      id: '',
    },
  });

  useEffect(() => {
    methods.setValue('email', data?.email);
    methods.setValue('name', data?.name);
    methods.setValue('id', data?.id);
  }, [data]);

  const onSubmit = (form: IMyProfileForm) => {
    dispatch(updateProfileForm(form));
  };

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <Stack width={'100%'} my={5}>
        <Stack sx={[styles.imageContainer, { bgcolor: theme.palette.secondary.light }]}>
          <Icon
            icon={'solar:user-bold'}
            color={theme.palette.primary.main}
            height={70}
            width={70}
          />
        </Stack>
      </Stack>
      <Stack>
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.fullName}
        </Typography>
        <RHFTextField
          data-testId="login.name"
          name="name"
          type="name"
          fullWidth
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_profile.svg`} />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          fontFamily={typography.fontFamily}
          fontSize={{ xs: '12px', md: '15px' }}
          fontWeight={typography.fontWeightRegular}
        >
          {Strings.field.emailAddress}
        </Typography>
        <RHFTextField
          data-testId="login.email"
          name="email"
          type="email"
          fullWidth
          disabled
          size="small"
          sx={{ marginBottom: 2, backgroundColor: theme.palette.grey[50], borderRadius: '10px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SvgColor fontSize={'10px'} src={`/assets/icons/ic_email.svg`} />
              </InputAdornment>
            ),
          }}
        />

        <LoadingButton
          variant="contained"
          type="submit"
          loading={loading}
          sx={{
            marginTop: { xs: 5, md: 10 },
            borderRadius: '50px',
          }}
        >
          {Strings.button.save}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
};

const styles = {
  imageContainer: {
    height: '120px',
    width: '120px',
    p: -1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 50,
    boxShadow: '0px 2px 5px rgba(73, 67, 67, 0.2)',
    mb: 2,
    alignSelf: 'center',
  },
};
export { MyProfile };
